import { nanoid } from 'nanoid';
import {
  APPEAL_TYPE_LABELS,
  AppealType,
  DCM_APPEAL_AXIS_OPTIONS,
  DcmAppeal,
  DcmCopy,
  DcmFunnelStageId,
  DcmFunnelStageMaster,
  DcmSegmentMaster,
  DcmSource,
  DcmViewModel,
  DcmWho,
  FUNNEL_STAGE_OPTIONS,
  ProductExtendedMetadata as KnowledgeExtendedMetadata,
  Segment,
  SegmentAppeal,
  migrateToSegments,
} from '../types/knowledge';
import { Product, ProductExtendedMetadata } from '../types/product';

const DEFAULT_SEGMENT_ID = 'segment-main';

// 既存ペルソナの空欄バックフィル用の決定的プール（who_id で安定選出＝リロードで変わらない）
const PERSONA_NAME_POOL = ['田村 さやか', '佐藤 健太', '鈴木 美咲', '高橋 亮', '中村 由紀', '小林 大輔', '加藤 彩', '渡辺 翔太'];
const PERSONA_TAGLINE_POOL = ['都心在住・IT企業勤務', '郊外在住・サービス業', '地方都市在住・小売業', '都心在住・専門職'];

const stableSeed = (key: string): number => {
  let hash = 0;
  for (let index = 0; index < key.length; index += 1) {
    hash = (hash * 31 + key.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const isFunnelStageId = (value?: string): value is DcmFunnelStageId =>
  value === 'latent' ||
  value === 'semi_latent' ||
  value === 'semi_manifest' ||
  value === 'manifest';

export const getDefaultFunnelStageMaster = (): DcmFunnelStageMaster[] =>
  FUNNEL_STAGE_OPTIONS.map((stage, index) => ({
    funnel_stage_id: stage.value,
    label: stage.label,
    sub: stage.sub,
    order: index,
  }));

/**
 * ⑤: 保存済み funnel_stage_master のラベル/subを、現行の FUNNEL_STAGE_OPTIONS で上書きする。
 * ID（funnel_stage_id）は保存データを信頼するが、ラベル文言は常に最新のものを表示する。
 * これにより、旧ラベル（例:「準潜在層」）で保存された既存商品でも、改名後は自動で
 * 新ラベル（「準顕在層」）に切り替わる（既存データのマイグレーションは不要）。
 */
const normalizeFunnelStageMaster = (stages: DcmFunnelStageMaster[]): DcmFunnelStageMaster[] =>
  stages.map((stage) => {
    const canonical = FUNNEL_STAGE_OPTIONS.find((option) => option.value === stage.funnel_stage_id);
    return canonical ? { ...stage, label: canonical.label, sub: canonical.sub } : stage;
  });

const normalizeAppealType = (type?: string): AppealType => {
  if (!type) return 'feature';
  if (type === 'functional') return 'feature';
  if (type === 'value') return 'benefit';
  if (type === 'trust') return 'trust';
  if (type in APPEAL_TYPE_LABELS) return type as AppealType;
  return 'feature';
};

const buildCopies = (appeal: SegmentAppeal | any): DcmCopy[] => {
  if (Array.isArray(appeal?.copies) && appeal.copies.length > 0) {
    return appeal.copies.map((copy: any, index: number) => ({
      id: copy.id || nanoid(),
      text: copy.text || '',
      use: copy.use ?? index === 0,
    }));
  }

  const copyText = appeal?.copy_example || appeal?.winning_copy || '';
  if (!copyText) {
    return [{ id: nanoid(), text: 'このペルソナに合わせたコピー案を追加してください。', use: true }];
  }

  return String(copyText)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 5)
    .map((text, index) => ({ id: nanoid(), text, use: index === 0 }));
};

const segmentToAppeals = (segment: Segment): DcmAppeal[] => {
  const appeals = segment.appeals?.length ? segment.appeals : [];
  if (appeals.length === 0) {
    return [
      {
        appeal_id: nanoid(),
        name: '訴求を追加',
        type: 'feature',
        appeal_axis: '基本性能',
        direction: '',
        copies: [{ id: nanoid(), text: 'コピー案を追加してください。', use: true }],
        order: 0,
      },
    ];
  }

  return appeals.map((appeal, index) => ({
    appeal_id: appeal.id || nanoid(),
    name: appeal.name || `訴求${index + 1}`,
    type: normalizeAppealType(appeal.type),
    appeal_axis: appeal.appeal_axis,
    direction: appeal.direction || '',
    copies: buildCopies(appeal),
    order: appeal.order ?? index,
  }));
};

const segmentToWho = (segment: Segment, index: number): DcmWho => {
  const funnelStageId = isFunnelStageId(segment.funnel_stage)
    ? segment.funnel_stage
    : FUNNEL_STAGE_OPTIONS[index % FUNNEL_STAGE_OPTIONS.length].value;

  return {
    who_id: `who-${segment.id || nanoid()}`,
    segment_id: segment.id || DEFAULT_SEGMENT_ID,
    funnel_stage_id: funnelStageId,
    who_name: segment.name || `ペルソナ${index + 1}`,
    demo: {
      gender: segment.gender,
      age_range: segment.age_range,
      region: segment.region,
      occupation: segment.occupation,
      purchasing_power: segment.purchasing_power,
      tech_literacy: segment.tech_literacy,
      life_stage: segment.life_stage,
    },
    persona: {
      story: segment.insights?.ideal_state || segment.insights?.pain_emotion,
      situation: segment.benefits?.summary,
      desire: segment.insights?.ideal_state,
      anxiety: segment.insights?.barriers,
      decision_context: segment.insights?.purchase_factor,
    },
    emotion_map: {
      pain_emotion: segment.insights?.pain_emotion,
      ideal_state: segment.insights?.ideal_state,
      blocking_emotion: segment.insights?.barriers,
      decision_factor: segment.insights?.purchase_factor,
    },
    value: {
      functional_value: segment.benefits?.functional,
      economic_value: segment.benefits?.economic,
      emotional_value: segment.benefits?.emotional,
      social_value: segment.benefits?.social || segment.benefits?.self_realization,
      summary: segment.benefits?.summary,
    },
    appeals: segmentToAppeals(segment),
    order: segment.order ?? index,
  };
};

const normalizeSegmentMaster = (segments: Segment[], meta?: ProductExtendedMetadata): DcmSegmentMaster[] => {
  if (meta?.segment_master?.length) {
    return meta.segment_master
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }

  const uniqueSegments = new Map<string, DcmSegmentMaster>();
  segments.forEach((segment, index) => {
    const segmentId = segment.id || `${DEFAULT_SEGMENT_ID}-${index}`;
    if (!uniqueSegments.has(segmentId)) {
      uniqueSegments.set(segmentId, {
        segment_id: segmentId,
        segment_name: segment.name || `セグメント${index + 1}`,
        definition: segment.occupation || segment.life_stage || '',
        usage_context: segment.insights?.purchase_factor || '',
        motivation: segment.insights?.ideal_state || '',
        common_anxiety: segment.insights?.barriers || '',
        order: segment.order ?? index,
      });
    }
  });

  return Array.from(uniqueSegments.values()).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
};

const collectSources = (meta?: ProductExtendedMetadata): DcmSource[] => {
  const sources: DcmSource[] = [];
  if (Array.isArray(meta?.source)) {
    sources.push(...meta!.source);
  }

  meta?.extraction_meta?.sources?.forEach((source) => {
    sources.push({ url: source.url, title: source.title });
  });

  meta?.positioning?.trust_proof?.forEach((proof: any) => {
    if (proof && typeof proof === 'object' && (proof.url || proof.source || proof.title)) {
      sources.push({
        url: proof.url,
        title: proof.title,
        source: proof.source,
        statement: proof.statement,
      });
    }
  });

  const deduped = new Map<string, DcmSource>();
  sources.forEach((source, index) => {
    const key = source.url || source.title || source.source || `source-${index}`;
    if (!deduped.has(key)) deduped.set(key, source);
  });

  return Array.from(deduped.values());
};

/**
 * ⑧⑨: 訴求軸（appeal_axis）が無い訴求のフォールバック値を、訴求タイプの代表サブカテゴリ名
 * （DCM_APPEAL_AXIS_OPTIONS の先頭）から補う。
 *
 * 商品登録時のURL初期抽出（ProductInfoExtractorService の target[].appeal）は
 * appeal_axis 自体を出力しないため、初期生成由来の訴求は appeal_axis が常に未設定になる
 * （手動で「訴求を追加」した訴求はAI生成時にappeal_axisが入るため表示に差異が出ていた）。
 */
const fallbackAppealAxis = (type: AppealType): string =>
  DCM_APPEAL_AXIS_OPTIONS[type]?.[0] ?? DCM_APPEAL_AXIS_OPTIONS.feature[0];

/**
 * 既存ペルソナの空欄を決定的な仮の値で埋める（過去に生成された名前・項目が空のカードも、
 * リロードで一貫して仮の名前・値で表示されるようにする）。既存の非空値は上書きしない。
 */
const completeWho = (who: DcmWho, segmentName: string, funnelLabel: string): DcmWho => {
  const seed = stableSeed(who.who_id || who.who_name || segmentName || 'who');
  const persona = who.persona || {};
  const demo = who.demo || {};
  const emotion = who.emotion_map || {};
  const value = who.value || {};
  const direction = `${segmentName}の${funnelLabel}に合わせ、課題認識と意思決定を前に進める。`;

  const fill = (current: string | undefined, fallback: string): string =>
    current && current.trim() ? current : fallback;

  return {
    ...who,
    who_name: fill(who.who_name, `${funnelLabel}の${segmentName}`),
    persona: {
      ...persona,
      name: fill(persona.name, PERSONA_NAME_POOL[seed % PERSONA_NAME_POOL.length]),
      age: fill(persona.age, `${28 + (seed % 20)}歳`),
      tagline: fill(persona.tagline, PERSONA_TAGLINE_POOL[seed % PERSONA_TAGLINE_POOL.length]),
      story: fill(persona.story, direction),
      situation: fill(persona.situation, `${segmentName}として情報収集中。`),
      desire: fill(persona.desire, '自分に合う選択肢を短時間で見極めたい。'),
      anxiety: fill(persona.anxiety, '失敗したくない、比較に時間をかけたくない。'),
      decision_context: fill(persona.decision_context, '信頼できる根拠と具体的な利用イメージがあると動きやすい。'),
    },
    demo: {
      ...demo,
      region: fill(demo.region, '都市部'),
      gender: fill(demo.gender, '問わず'),
      occupation: fill(demo.occupation, 'オフィスワーカー'),
      purchasing_power: fill(demo.purchasing_power, '中〜高'),
      tech_literacy: fill(demo.tech_literacy, '中程度'),
      life_stage: fill(demo.life_stage, '働き盛り'),
    },
    emotion_map: {
      ...emotion,
      pain_emotion: fill(emotion.pain_emotion, '今の方法では非効率だと感じている。'),
      ideal_state: fill(emotion.ideal_state, '納得感を持ってすぐ次の行動に進める。'),
      blocking_emotion: fill(emotion.blocking_emotion, '費用対効果や導入負担への不安。'),
      decision_factor: fill(emotion.decision_factor, '実績、わかりやすさ、導入後の成果。'),
    },
    value: {
      ...value,
      functional_value: fill(value.functional_value, '必要な情報が整理され、比較しやすい。'),
      economic_value: fill(value.economic_value, '検討や作業にかかる時間を削減できる。'),
      emotional_value: fill(value.emotional_value, '迷いが減り、安心して判断できる。'),
      social_value: fill(value.social_value, '周囲に説明しやすい選定理由が得られる。'),
      summary: fill(value.summary, '判断の不安を減らし、次の行動を後押しする。'),
    },
    appeals: (who.appeals?.length ? who.appeals : []).map((appeal) => ({
      ...appeal,
      appeal_axis: appeal.appeal_axis || fallbackAppealAxis(appeal.type),
    })),
  };
};

export const productToDcmViewModel = (product?: Product | null): DcmViewModel => {
  const meta = product?.extended_metadata;
  const fallbackSegments = meta?.segments?.length
    ? meta.segments
    : migrateToSegments(meta?.target, meta?.benefits, meta?.appeal as any);

  const segmentMaster = normalizeSegmentMaster(fallbackSegments, meta);
  const rawWho = meta?.who?.length
    ? meta.who.map((item, index) => ({
        ...item,
        funnel_stage_id: isFunnelStageId(item.funnel_stage_id) ? item.funnel_stage_id : 'latent',
        appeals: item.appeals?.length ? item.appeals : segmentToAppeals(fallbackSegments[index] || fallbackSegments[0]),
      }))
    : fallbackSegments.map(segmentToWho);

  // 名前・項目が空の（過去分含む）ペルソナを決定的な仮の値でバックフィルして表示一貫性を担保
  const segmentNameById = new Map(segmentMaster.map((segment) => [segment.segment_id, segment.segment_name]));
  const who = rawWho.map((item) => {
    const segmentName = segmentNameById.get(item.segment_id) || 'セグメント';
    const funnelLabel =
      FUNNEL_STAGE_OPTIONS.find((stage) => stage.value === item.funnel_stage_id)?.label || 'ファネル';
    return completeWho(item, segmentName, funnelLabel);
  });

  return {
    segmentMaster,
    // ⑤: funnel_stage_master は保存データにラベル文字列も一緒に入るため、旧ラベルで
    // 保存された既存商品（例: semi_latent="準潜在層"）がそのまま古い表示に固定されない
    // よう、表示時は常に ID→FUNNEL_STAGE_OPTIONS の現行ラベルを正とする（IDのみ信頼する）。
    funnelStageMaster: normalizeFunnelStageMaster(
      meta?.funnel_stage_master?.length ? meta.funnel_stage_master : getDefaultFunnelStageMaster(),
    ),
    who,
    source: collectSources(meta),
  };
};

export const dcmWhoToSegment = (who: DcmWho, segment: DcmSegmentMaster, index: number): Segment => ({
  // id は who 単位で一意にする。segment_id を使うと同一セグメントに複数ペルソナがいる場合に
  // segments[] の id が衝突し、id を鍵に find() する商品選択モーダルで2人目以降の
  // 訴求軸が選べなくなる（同名の選択肢が並ぶだけになる）。
  id: who.who_id || who.segment_id || segment.segment_id || nanoid(),
  name: segment.segment_name || who.who_name || `セグメント${index + 1}`,
  funnel_stage: who.funnel_stage_id,
  gender: who.demo?.gender || '',
  age_range: who.demo?.age_range || '',
  region: who.demo?.region || '',
  occupation: who.demo?.occupation || '',
  purchasing_power: who.demo?.purchasing_power || '',
  tech_literacy: who.demo?.tech_literacy || '',
  life_stage: who.demo?.life_stage || '',
  insights: {
    pain_emotion: who.emotion_map?.pain_emotion || '',
    ideal_state: who.emotion_map?.ideal_state || who.persona?.desire || '',
    barriers: who.emotion_map?.blocking_emotion || who.persona?.anxiety || '',
    purchase_factor: who.emotion_map?.decision_factor || who.persona?.decision_context || '',
  },
  benefits: {
    functional: who.value?.functional_value || '',
    economic: who.value?.economic_value || '',
    emotional: who.value?.emotional_value || '',
    social: who.value?.social_value || '',
    self_realization: who.value?.social_value || '',
    summary: who.value?.summary || '',
  },
  appeals: who.appeals.map((appeal, appealIndex) => ({
    id: appeal.appeal_id || nanoid(),
    name: appeal.name || `訴求${appealIndex + 1}`,
    type: normalizeAppealType(appeal.type),
    appeal_axis: appeal.appeal_axis,
    direction: appeal.direction || '',
    copy_example: appeal.copies?.map((copy) => copy.text).filter(Boolean).join('\n') || '',
    copies: appeal.copies || [],
    order: appeal.order ?? appealIndex,
  })),
  order: who.order ?? index,
});

export const mergeDcmIntoMetadata = (
  metadata: ProductExtendedMetadata | KnowledgeExtendedMetadata | undefined,
  viewModel: DcmViewModel,
): ProductExtendedMetadata => {
  const segmentById = new Map(viewModel.segmentMaster.map((segment) => [segment.segment_id, segment]));
  const fallbackSegment = viewModel.segmentMaster[0] || {
    segment_id: DEFAULT_SEGMENT_ID,
    segment_name: 'メインセグメント',
    order: 0,
  };

  const segments = viewModel.who.map((who, index) =>
    dcmWhoToSegment(who, segmentById.get(who.segment_id) || fallbackSegment, index),
  );

  return {
    ...(metadata as ProductExtendedMetadata),
    segment_master: viewModel.segmentMaster,
    funnel_stage_master: viewModel.funnelStageMaster,
    who: viewModel.who,
    source: viewModel.source,
    segments,
  };
};
